import TableOfContents from "@/components/base/TableOfContents";

const legalSections = [
  { id: "disclaimer", label: "Disclaimer", number: 1 },
  { id: "privacy", label: "Privacy Policy", number: 2 },
  { id: "affiliate", label: "Affiliate Disclosure", number: 3 },
  { id: "cookies", label: "Cookie Policy", number: 4 },
];

export default function Legal() {
  return (
    <div className="min-h-screen bg-background-50 pt-20 md:pt-24">
      <div className="w-full px-4 md:px-6 lg:px-8 py-16 md:py-20">
        <div className="max-w-5xl mx-auto flex gap-10 lg:gap-14">
          {/* Sticky sidebar table of contents - desktop only */}
          <aside className="hidden lg:block w-48 flex-shrink-0">
            <TableOfContents sections={legalSections} />
          </aside>

          {/* Main content */}
          <div className="flex-1 min-w-0 max-w-3xl">
            <nav className="text-sm text-foreground-500 mb-4" aria-label="Breadcrumb">
              <ol className="flex items-center gap-1.5">
                <li>
                  <a href="/" className="hover:text-primary-700 transition-colors">Home</a>
                </li>
                <li aria-hidden="true">
                  <span className="text-foreground-400">&rsaquo;</span>
                </li>
                <li>
                  <span className="text-foreground-700 font-medium">Legal</span>
                </li>
              </ol>
            </nav>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-10">
              Legal Information
            </h1>

            {/* Inline TOC for mobile / quick-access pills */}
            <nav className="bg-background-100 border border-background-200 rounded-xl p-4 md:p-5 mb-10 lg:hidden" aria-label="Table of contents">
              <h4 className="text-sm font-semibold text-foreground-700 uppercase tracking-wider mb-3">
                Contents
              </h4>
              <ol className="flex flex-wrap gap-2">
                {legalSections.map((section) => (
                  <li key={section.id}>
                    <a href={`#${section.id}`} className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-full bg-background-50 border border-background-200 text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap">
                      <span className="w-5 h-5 flex items-center justify-center mr-1.5 text-xs font-bold rounded-full bg-secondary-100 text-secondary-800">{section.number}</span>
                      {section.label}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            <section id="disclaimer" className="mb-10 scroll-mt-24 md:scroll-mt-28">
              <h2 className="text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
                Disclaimer
              </h2>
              <p className="text-sm text-foreground-500 mb-6 italic">
                Last updated: March 2025
              </p>

              <h3 className="text-base font-semibold text-foreground-950 mb-2">
                General Information Only
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                The information provided on dentalsafetyabroad.com is for general informational and educational purposes only. It does not constitute medical, dental, or professional advice of any kind. Nothing on this website should be relied upon as a substitute for professional dental consultation, diagnosis, or treatment.
              </p>

              <h3 className="text-base font-semibold text-foreground-950 mb-2">
                Not a Medical or Dental Provider
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                Dental Safety Abroad is an independent information resource. We are not a dental clinic, dental practice, healthcare provider, medical adviser, dental travel agency, or treatment broker. We do not assess, diagnose, treat, or refer patients for any dental condition or procedure.
              </p>

              <h3 className="text-base font-semibold text-foreground-950 mb-2">
                Always Seek Professional Advice
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                Before making any decision about dental treatment — in the UK or abroad — you should consult a qualified, registered dental professional who can evaluate your specific situation. Do not disregard or delay seeking professional dental advice because of something you have read on this website.
              </p>

              <h3 className="text-base font-semibold text-foreground-950 mb-2">
                Accuracy of Information
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                Whilst we make every reasonable effort to ensure the accuracy of the information on this website, we make no representations or warranties of any kind, express or implied, about the completeness, accuracy, reliability, suitability, or availability of the information, products, services, or related graphics contained on the website for any purpose.
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                The dental tourism market changes rapidly. Prices, regulations, clinic details, and country conditions may change without notice. Information should be independently verified before being relied upon.
              </p>

              <h3 className="text-base font-semibold text-foreground-950 mb-2">
                Cost Estimates
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                All pricing estimates published on this website are approximate, illustrative, and based on general market research. They should not be treated as quotes. Individual clinic prices vary significantly. Always obtain a written, itemised quote from a clinic before making any financial commitment.
              </p>

              <h3 className="text-base font-semibold text-foreground-950 mb-2">
                No Clinic Endorsement
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                Dental Safety Abroad does not endorse, recommend, or vouch for any specific dental clinic, dentist, laboratory, or treatment provider. Any references to clinics or destinations are for illustrative purposes only.
              </p>

              <h3 className="text-base font-semibold text-foreground-950 mb-2">
                Limitation of Liability
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                To the fullest extent permitted by law, Dental Safety Abroad, its operators, and its contributors shall not be liable for any direct, indirect, incidental, consequential, or punitive damages arising from your use of, or reliance on, any information provided on this website.
              </p>

              <h3 className="text-base font-semibold text-foreground-950 mb-2">
                External Links
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                This website may contain links to third-party websites. These links are provided for your convenience only. We have no control over the content of those sites and accept no responsibility for them or for any loss or damage that may arise from your use of them.
              </p>
            </section>

            <section id="privacy" className="mb-10 scroll-mt-24 md:scroll-mt-28">
              <h2 className="text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
                Privacy Policy
              </h2>
              <p className="text-sm text-foreground-500 mb-6 italic">
                Last updated: July 2026
              </p>

              <h3 className="text-base font-semibold text-foreground-950 mb-2">
                Who We Are
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                Dental Safety Abroad (&ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;) operates this website. We are the data controller for any personal information collected through this site. If you have questions about this policy, you can reach us via our <a href="/contact" className="text-primary-600 hover:text-primary-700 underline">contact page</a>.
              </p>

              <h3 className="text-base font-semibold text-foreground-950 mb-2">
                What Data We Collect
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-2">
                We collect only the information you voluntarily provide, which may include:
              </p>
              <ul className="list-disc list-inside text-sm text-foreground-600 leading-relaxed mb-4 space-y-1">
                <li><strong>Quiz responses</strong> — your answers to our destination-matching quiz, which help us provide personalised recommendations</li>
                <li><strong>Contact form submissions</strong> — your name, email address, and any message content you choose to share</li>
                <li><strong>Newsletter sign-ups</strong> — your email address when you subscribe to receive updates</li>
                <li><strong>Usage data</strong> — anonymised information about how you interact with our site, collected through standard analytics tools</li>
              </ul>

              <h3 className="text-base font-semibold text-foreground-950 mb-2">
                Legal Basis for Processing
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                Under the UK GDPR and EU GDPR, we rely on the following lawful bases for processing your personal data:
              </p>
              <ul className="list-disc list-inside text-sm text-foreground-600 leading-relaxed mb-4 space-y-1">
                <li><strong>Consent</strong> — when you voluntarily submit information through our quiz, contact form, or newsletter sign-up, you consent to us processing that data for the stated purpose</li>
                <li><strong>Legitimate interest</strong> — we process anonymised usage data to improve our content, understand which destinations and topics interest our readers, and enhance the user experience</li>
              </ul>

              <h3 className="text-base font-semibold text-foreground-950 mb-2">
                How We Use Your Data
              </h3>
              <ul className="list-disc list-inside text-sm text-foreground-600 leading-relaxed mb-4 space-y-1">
                <li>To provide destination and treatment recommendations based on your quiz answers</li>
                <li>To respond to enquiries submitted through our contact form</li>
                <li>To send newsletters and updates if you have subscribed (you can unsubscribe at any time)</li>
                <li>To analyse aggregated, anonymised usage patterns and improve our content</li>
              </ul>

              <h3 className="text-base font-semibold text-foreground-950 mb-2">
                Cookies
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                We use essential cookies to ensure the website functions properly. We may also use analytics cookies to understand how visitors use our site — these collect anonymised data and do not identify you personally. You can control cookie preferences through your browser settings at any time.
              </p>

              <h3 className="text-base font-semibold text-foreground-950 mb-2">
                Data Sharing &amp; Third Parties
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                We do not sell, rent, or trade your personal data. We may share data with trusted third-party service providers who help us operate the website (such as hosting providers and analytics platforms), but only to the extent necessary and under appropriate data processing agreements. We never share your personal information with dental clinics or treatment providers.
              </p>

              <h3 className="text-base font-semibold text-foreground-950 mb-2">
                Data Retention
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                We retain your personal data only for as long as necessary to fulfil the purpose for which it was collected. Quiz and contact form data is typically retained for up to 24 months unless you request earlier deletion. Anonymised usage data may be retained indefinitely for analytical purposes.
              </p>

              <h3 className="text-base font-semibold text-foreground-950 mb-2">
                Your Rights Under GDPR
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-2">
                As a data subject under UK GDPR and EU GDPR, you have the following rights:
              </p>
              <ul className="list-disc list-inside text-sm text-foreground-600 leading-relaxed mb-4 space-y-1">
                <li><strong>Right of access</strong> — request a copy of the personal data we hold about you</li>
                <li><strong>Right to rectification</strong> — ask us to correct any inaccurate or incomplete data</li>
                <li><strong>Right to erasure</strong> — request that we delete your personal data (&ldquo;right to be forgotten&rdquo;)</li>
                <li><strong>Right to restrict processing</strong> — ask us to limit how we use your data in certain circumstances</li>
                <li><strong>Right to data portability</strong> — receive your data in a structured, commonly used format and transfer it to another controller</li>
                <li><strong>Right to object</strong> — object to processing based on legitimate interests or direct marketing</li>
                <li><strong>Right to withdraw consent</strong> — withdraw your consent at any time where processing is based on consent</li>
              </ul>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                To exercise any of these rights, please <a href="/contact" className="text-primary-600 hover:text-primary-700 underline">contact us</a>. We will respond within one month of receiving your request. There is no fee for exercising these rights unless the request is manifestly unfounded or excessive.
              </p>

              <h3 className="text-base font-semibold text-foreground-950 mb-2">
                Complaints
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                If you believe your data protection rights have been violated, you have the right to lodge a complaint with the UK Information Commissioner&rsquo;s Office (ICO) or your local data protection supervisory authority within the EU.
              </p>

              <h3 className="text-base font-semibold text-foreground-950 mb-2">
                Changes to This Policy
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                We may update this privacy policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically to stay informed about how we protect your data.
              </p>
            </section>

            <section id="affiliate" className="scroll-mt-24 md:scroll-mt-28">
              <h2 className="text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
                Affiliate Disclosure
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                Dental Safety Abroad is reader-supported. We may earn a commission when you click through links on our site or take certain actions — at no extra cost to you. This helps us keep our content free and independent.
              </p>
              <h3 className="text-base font-semibold text-foreground-950 mb-2">
                Blog Posts &amp; Content
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                Some articles on our blog may contain affiliate links to third-party products, services, or providers. When we include such links, we disclose their presence within the article itself so you can make informed decisions. Our editorial content is never influenced by affiliate partnerships — our research, ratings, and recommendations are based on independent analysis and user feedback.
              </p>
              <h3 className="text-base font-semibold text-foreground-950 mb-2">
                Product &amp; Service Recommendations
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                We may recommend travel insurance, dental financing options, accommodation booking platforms, or other services that we believe genuinely add value to your dental tourism experience. If you purchase through our links, we may earn a referral fee. We only recommend services we have researched and would use ourselves.
              </p>
              <h3 className="text-base font-semibold text-foreground-950 mb-2">
                Clinic Affiliations
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                Dental Safety Abroad maintains no clinic affiliations and receives no commissions from dental providers. We do not accept payment from clinics for favourable placement, reviews, or recommendations. Our destination comparisons and clinic safety advice are produced independently.
              </p>
              <h3 className="text-base font-semibold text-foreground-950 mb-2">
                Your Trust Matters
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Transparency is core to what we do. If you ever have questions about how we make money, which links are affiliate links, or whether a recommendation is influenced by a partnership, please <a href="/contact" className="text-primary-600 hover:text-primary-700 underline">contact us</a> — we&rsquo;re happy to explain.
              </p>
            </section>

            <section id="cookies" className="mb-10 scroll-mt-24 md:scroll-mt-28">
              <h2 className="text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
                Cookie Policy
              </h2>
              <p className="text-sm text-foreground-500 mb-6 italic">
                Last updated: March 2025
              </p>

              <h3 className="text-base font-semibold text-foreground-950 mb-2">
                What Are Cookies
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                Cookies are small text files stored on your device when you visit a website. They help the website function, remember preferences, and (in some cases) collect analytics data.
              </p>

              <h3 className="text-base font-semibold text-foreground-950 mb-2">
                Cookies We Use
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-2">
                This website currently uses minimal cookies:
              </p>
              <ul className="list-disc list-inside text-sm text-foreground-600 leading-relaxed mb-4 space-y-1">
                <li><strong>Essential cookies:</strong> Required for the website to function (e.g., form security tokens). These cannot be disabled.</li>
                <li><strong>Analytics cookies:</strong> If we use an analytics tool, it may set cookies to track anonymised usage data. No personally identifiable information is collected.</li>
              </ul>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                We do not use advertising cookies, retargeting cookies, or social media tracking cookies.
              </p>

              <h3 className="text-base font-semibold text-foreground-950 mb-2">
                Managing Cookies
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                You can control and/or delete cookies from your browser settings. Disabling essential cookies may affect website functionality. For guidance, visit <a href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 underline">allaboutcookies.org</a>.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}